import React, { useState, useMemo } from 'react';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { useLiveData } from '../../hooks/useLiveData';
import { ipApi } from '../../services/api';

// ─── Static Reference Data ────────────────────────────────────────────────────

const SURGERY_TYPES = [
  'Appendectomy', 'LSCS', 'Laparoscopic Cholecystectomy', 'Knee Replacement',
  'Hip Replacement', 'Cataract Surgery', 'Tonsillectomy', 'Hernia Repair',
  'Hysterectomy', 'Prostatectomy', 'TURP', 'Thyroidectomy', 'Mastectomy',
  'CABG', 'Myringotomy', 'Septoplasty', 'FESS', 'Tympanoplasty', 'Colostomy',
  'Colectomy', 'Bowel Resection', 'Splenectomy', 'Nephrectomy', 'Pyeloplasty',
  'Ureteroscopy', 'PCNL', 'Craniotomy', 'VP Shunt', 'Discectomy', 'Laminectomy',
  'Varicose Vein Surgery', 'Amputation', 'Skin Grafting', 'Debridement',
];

const OT_ROOMS = ['OT-1', 'OT-2', 'OT-3', 'OT-4'];

const ANAESTHESIA_TYPES = ['GA', 'Spinal', 'Epidural', 'Local', 'IV Sedation'];

const PRIORITIES = ['Elective', 'Emergency', 'Semi-urgent'];

const TODAY = new Date().toISOString().slice(0, 10);

// ─── Helper Utilities ─────────────────────────────────────────────────────────

const BLANK_FORM = {
  patientId: '', surgeryType: '', surgeon: '', anaesthetist: '',
  otRoom: '', date: '', time: '', anaesthesia: '', priority: '',
  duration: '', preOpNotes: '',
};

const BLANK_NOTES = {
  findings: '', procedureDetails: '', implantsUsed: '', bloodLoss: '',
  ivFluids: '', specimensSent: '', additionalNotes: '',
};

function priorityBadge(priority) {
  const map = {
    Emergency:   { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
    Elective:    { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
    'Semi-urgent':{ bg: '#ffedd5', color: '#c2410c', border: '#fdba74' },
  };
  const s = map[priority] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, letterSpacing: 0.3,
    }}>
      {priority}
    </span>
  );
}

function statusBadge(status) {
  const map = {
    Completed:   { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
    'In Progress':{ bg: '#fef9c3', color: '#a16207', border: '#fde047' },
    Scheduled:   { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, letterSpacing: 0.3,
    }}>
      {status}
    </span>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 22 }}>{icon}</span> {title}
      </h2>
      {subtitle && <p style={{ margin: '4px 0 0 30px', fontSize: 13, color: '#6b7280' }}>{subtitle}</p>}
    </div>
  );
}

// ─── Tab: OT Schedule ─────────────────────────────────────────────────────────

function OtScheduleTab({ surgeries }) {
  const todaySurgeries = useMemo(
    () => surgeries.filter(s => s.date === TODAY).sort((a, b) => a.time.localeCompare(b.time)),
    [surgeries]
  );

  return (
    <div>
      <SectionHeader icon="🗓️" title="Today's OT Schedule" subtitle={`Date: ${TODAY} — ${todaySurgeries.length} surgeries planned`} />
      <div className="info-banner" style={{ marginBottom: 16 }}>
        <span>🏥</span>&nbsp; Showing all surgeries scheduled for <strong>today ({TODAY})</strong>.
        Status updates in real-time as procedures progress.
      </div>
      {todaySurgeries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <p>No surgeries scheduled for today.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f9ff', borderBottom: '2px solid #e5e7eb' }}>
                  {['OT Room','Time','Patient (IPD)','Surgery','Surgeon','Anaesthesia','Priority','Status','Duration'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {todaySurgeries.map((s, i) => (
                  <tr key={s.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '11px 14px', fontWeight: 700, color: '#4f46e5' }}>
                      <span className="emr-chip" style={{ background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd' }}>{s.ot}</span>
                    </td>
                    <td style={{ padding: '11px 14px', fontWeight: 600, fontFamily: 'monospace', fontSize: 14 }}>{s.time}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{s.patient}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{s.ipd} · {s.age}y</div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span className="emr-chip">{s.surgery}</span>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: 12 }}>{s.surgeon}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <span className="emr-chip" style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>{s.anaesthesia}</span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>{priorityBadge(s.priority)}</td>
                    <td style={{ padding: '11px 14px' }}>{statusBadge(s.status)}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 600, color: '#374151' }}>{s.duration} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* OT Room Status Summary */}
      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#374151' }}>🏨 OT Room Overview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {OT_ROOMS.map(room => {
            const roomSurgeries = todaySurgeries.filter(s => s.ot === room);
            const active = roomSurgeries.find(s => s.status === 'In Progress');
            const pending = roomSurgeries.filter(s => s.status === 'Scheduled').length;
            const done = roomSurgeries.filter(s => s.status === 'Completed').length;
            const isActive = !!active;
            return (
              <div key={room} className="card" style={{
                padding: '16px 18px',
                borderLeft: `4px solid ${isActive ? '#f59e0b' : roomSurgeries.length ? '#6366f1' : '#e5e7eb'}`,
              }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{room}</div>
                {roomSurgeries.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>No surgeries today</div>
                ) : (
                  <>
                    {isActive && <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginBottom: 4 }}>🔴 In Progress: {active.surgery}</div>}
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {done > 0 && <span>✅ {done} done &nbsp;</span>}
                      {pending > 0 && <span>🕐 {pending} pending</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Book Surgery ────────────────────────────────────────────────────────

function BookSurgeryTab({ onBook, admissions = [] }) {
  const [form, setForm] = useState(BLANK_FORM);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.patientId)    e.patientId    = 'Please select a patient.';
    if (!form.surgeryType)  e.surgeryType  = 'Please select a surgery type.';
    if (!form.surgeon)      e.surgeon      = 'Surgeon name is required.';
    if (!form.anaesthetist) e.anaesthetist = 'Anaesthetist name is required.';
    if (!form.otRoom)       e.otRoom       = 'Select an OT room.';
    if (!form.date)         e.date         = 'Date is required.';
    if (!form.time)         e.time         = 'Time is required.';
    if (!form.anaesthesia)  e.anaesthesia  = 'Select anaesthesia type.';
    if (!form.priority)     e.priority     = 'Select priority.';
    if (!form.duration || isNaN(Number(form.duration)) || Number(form.duration) <= 0)
      e.duration = 'Enter valid duration in minutes.';
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const patient = admissions.find(p => p.id === form.patientId);
    const newEntry = {
      id: 'OT' + String(Date.now()).slice(-4),
      patient: patient.name, age: patient.age, ipd: patient.id,
      surgery: form.surgeryType, surgeon: form.surgeon,
      anaesthetist: form.anaesthetist, ot: form.otRoom,
      date: form.date, time: form.time, anaesthesia: form.anaesthesia,
      priority: form.priority, duration: Number(form.duration),
      status: 'Scheduled', outcome: '-', bloodLoss: '-',
    };
    onBook(newEntry);
    setSubmitted(true);
    setForm(BLANK_FORM);
    setTimeout(() => setSubmitted(false), 3500);
  };

  const Field = ({ label, error, hint, children }) => (
    <div className="field">
      <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13, color: '#374151' }}>{label}</label>
      {children}
      {error && <div className="error" style={{ marginTop: 4 }}>{error}</div>}
      {hint && !error && <div className="hint" style={{ marginTop: 3 }}>{hint}</div>}
    </div>
  );

  return (
    <div>
      <SectionHeader icon="📝" title="Book a Surgery" subtitle="Schedule a new OT procedure for an admitted IPD patient" />

      {submitted && (
        <div className="info-banner" style={{ marginBottom: 16, background: '#dcfce7', border: '1px solid #86efac', color: '#166534' }}>
          ✅ Surgery booked successfully! The case has been added to the OT Register.
        </div>
      )}

      <div className="card" style={{ padding: 24 }}>
        <div className="form-grid">
          <Field label="Patient (IPD)" error={errors.patientId} hint="Select from currently admitted patients">
            <select
              className="input"
              value={form.patientId}
              onChange={e => set('patientId', e.target.value)}
            >
              <option value="">— Select Patient —</option>
              {admissions.length === 0 && (
                <option value="" disabled>Loading admissions…</option>
              )}
              {admissions.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.gender}/{p.age}) — {p.id}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Surgery Type" error={errors.surgeryType}>
            <select
              className="input"
              value={form.surgeryType}
              onChange={e => set('surgeryType', e.target.value)}
            >
              <option value="">— Select Surgery —</option>
              {SURGERY_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>

          <Field label="Surgeon" error={errors.surgeon}>
            <input
              className="input"
              type="text"
              placeholder="e.g. Dr. Anita Sharma"
              value={form.surgeon}
              onChange={e => set('surgeon', e.target.value)}
            />
          </Field>

          <Field label="Anaesthetist" error={errors.anaesthetist}>
            <input
              className="input"
              type="text"
              placeholder="e.g. Dr. Ramesh Verma"
              value={form.anaesthetist}
              onChange={e => set('anaesthetist', e.target.value)}
            />
          </Field>

          <Field label="OT Room" error={errors.otRoom}>
            <select
              className="input"
              value={form.otRoom}
              onChange={e => set('otRoom', e.target.value)}
            >
              <option value="">— Select OT Room —</option>
              {OT_ROOMS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>

          <Field label="Anaesthesia Type" error={errors.anaesthesia}>
            <select
              className="input"
              value={form.anaesthesia}
              onChange={e => set('anaesthesia', e.target.value)}
            >
              <option value="">— Select Type —</option>
              {ANAESTHESIA_TYPES.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </Field>

          <Field label="Date" error={errors.date}>
            <input
              className="input"
              type="date"
              value={form.date}
              onChange={e => set('date', e.target.value)}
            />
          </Field>

          <Field label="Time" error={errors.time}>
            <input
              className="input"
              type="time"
              value={form.time}
              onChange={e => set('time', e.target.value)}
            />
          </Field>

          <Field label="Priority" error={errors.priority}>
            <select
              className="input"
              value={form.priority}
              onChange={e => set('priority', e.target.value)}
            >
              <option value="">— Select Priority —</option>
              {PRIORITIES.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </Field>

          <Field label="Estimated Duration (minutes)" error={errors.duration} hint="Enter expected surgery duration in minutes">
            <input
              className="input"
              type="number"
              min="1"
              placeholder="e.g. 90"
              value={form.duration}
              onChange={e => set('duration', e.target.value)}
            />
          </Field>
        </div>

        <div style={{ marginTop: 16 }}>
          <Field label="Pre-op Notes" error={errors.preOpNotes} hint="Optional — include any relevant pre-operative instructions or observations">
            <textarea
              className="input"
              rows={3}
              placeholder="Enter any pre-operative notes, allergies, or special instructions..."
              value={form.preOpNotes}
              onChange={e => set('preOpNotes', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </Field>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
          <button className="btn btn-primary" onClick={handleSubmit}>
            🗓️ Book Surgery
          </button>
          <button className="btn btn-secondary" onClick={() => { setForm(BLANK_FORM); setErrors({}); }}>
            ↺ Reset Form
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: OT Register ─────────────────────────────────────────────────────────

function OtRegisterTab({ surgeries, updateSurgeryStatus }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const filtered = useMemo(() => {
    return surgeries
      .filter(s => {
        const q = search.toLowerCase();
        return !q || s.patient.toLowerCase().includes(q) || s.surgery.toLowerCase().includes(q)
          || s.surgeon.toLowerCase().includes(q) || s.id.toLowerCase().includes(q);
      })
      .filter(s => statusFilter === 'All' || s.status === statusFilter)
      .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  }, [surgeries, search, statusFilter]);

  return (
    <div>
      <SectionHeader icon="📒" title="OT Register" subtitle={`Complete surgical log — ${surgeries.length} total entries`} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="input"
          type="text"
          placeholder="🔍  Search by patient, surgery, surgeon, ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 220, maxWidth: 360 }}
        />
        <div className="period-tabs" style={{ display: 'flex', gap: 4 }}>
          {['All', 'Scheduled', 'In Progress', 'Completed'].map(st => (
            <button
              key={st}
              className={`btn btn-sm ${statusFilter === st ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setStatusFilter(st)}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#f8f9ff', borderBottom: '2px solid #e5e7eb' }}>
                {['Case ID','Date','Time','OT','Patient','Surgery','Surgeon','Anaesthetist','Anaesthesia','Priority','Duration','Status','Outcome','Blood Loss','Actions'].map(h => (
                  <th key={h} style={{ padding: '11px 13px', textAlign: 'left', fontWeight: 700, color: '#374151', fontSize: 11.5, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={14} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                    No records match your search / filter.
                  </td>
                </tr>
              ) : (
                filtered.map((s, i) => (
                  <tr key={s.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 13px', fontWeight: 700, color: '#4f46e5', fontFamily: 'monospace' }}>{s.id}</td>
                    <td style={{ padding: '10px 13px', whiteSpace: 'nowrap' }}>{s.date}</td>
                    <td style={{ padding: '10px 13px', fontFamily: 'monospace' }}>{s.time}</td>
                    <td style={{ padding: '10px 13px' }}>
                      <span className="emr-chip" style={{ background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd' }}>{s.ot}</span>
                    </td>
                    <td style={{ padding: '10px 13px' }}>
                      <div style={{ fontWeight: 600 }}>{s.patient}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{s.ipd} · {s.age}y</div>
                    </td>
                    <td style={{ padding: '10px 13px' }}><span className="emr-chip">{s.surgery}</span></td>
                    <td style={{ padding: '10px 13px', whiteSpace: 'nowrap', fontSize: 12 }}>{s.surgeon}</td>
                    <td style={{ padding: '10px 13px', whiteSpace: 'nowrap', fontSize: 12 }}>{s.anaesthetist}</td>
                    <td style={{ padding: '10px 13px' }}>
                      <span className="emr-chip" style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>{s.anaesthesia}</span>
                    </td>
                    <td style={{ padding: '10px 13px' }}>{priorityBadge(s.priority)}</td>
                    <td style={{ padding: '10px 13px', fontWeight: 600 }}>{s.duration} min</td>
                    <td style={{ padding: '10px 13px' }}>{statusBadge(s.status)}</td>
                    <td style={{ padding: '10px 13px', fontSize: 12, color: s.outcome === 'Successful' ? '#166534' : '#6b7280', fontWeight: s.outcome === 'Successful' ? 700 : 400 }}>
                      {s.outcome === 'Successful' ? '✅ ' + s.outcome : s.outcome}
                    </td>
                    <td style={{ padding: '10px 13px', fontSize: 12, fontFamily: 'monospace' }}>{s.bloodLoss}</td>
                    <td style={{ padding: '10px 13px' }}>
                      {updateSurgeryStatus && s.status === 'Scheduled' && (
                        <button className="btn btn-sm btn-primary" style={{ fontSize: 10, whiteSpace: 'nowrap' }}
                          onClick={() => updateSurgeryStatus(s.id, 'In Progress')}>
                          ▶ Start
                        </button>
                      )}
                      {updateSurgeryStatus && s.status === 'In Progress' && (
                        <button className="btn btn-sm btn-primary" style={{ fontSize: 10, background: '#10b981', border: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => updateSurgeryStatus(s.id, 'Completed', { outcome: 'Successful' })}>
                          ✓ Complete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '10px 16px', background: '#f8f9ff', borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280' }}>
          Showing {filtered.length} of {surgeries.length} records
        </div>
      </div>
    </div>
  );
}

// ─── Tab: OT Notes ────────────────────────────────────────────────────────────

function OtNotesTab({ surgeries }) {
  const { records: otNotes, create: createNote, patch: patchNote } = useHmsRecords('ot-note');
  const completedSurgeries = useMemo(
    () => surgeries.filter(s => s.status === 'Completed'),
    [surgeries]
  );

  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(BLANK_NOTES);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedSurgery = completedSurgeries.find(s => s.id === selectedId);

  const handleSelect = (id) => {
    setSelectedId(id);
    setSaved(false);
    const existing = otNotes.find(n => n.surgeryId === id);
    if (existing) {
      setForm({
        findings:         existing.findings         || '',
        procedureDetails: existing.procedureDetails || '',
        implantsUsed:     existing.implantsUsed     || '',
        bloodLoss:        existing.bloodLoss        || '',
        ivFluids:         existing.ivFluids         || '',
        specimensSent:    existing.specimensSent    || '',
        additionalNotes:  existing.additionalNotes  || '',
      });
    } else {
      setForm(BLANK_NOTES);
    }
  };

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const existing = otNotes.find(n => n.surgeryId === selectedId);
      if (existing) {
        await patchNote(existing.id, { ...form });
      } else {
        await createNote({ surgeryId: selectedId, ...form });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const NoteField = ({ label, field, multiline = false, rows = 3 }) => (
    <div className="field">
      <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13, color: '#374151' }}>{label}</label>
      {multiline ? (
        <textarea
          className="input"
          rows={rows}
          value={form[field]}
          onChange={e => set(field, e.target.value)}
          style={{ resize: 'vertical' }}
          placeholder={`Enter ${label.toLowerCase()}...`}
          disabled={!selectedSurgery}
        />
      ) : (
        <input
          className="input"
          type="text"
          value={form[field]}
          onChange={e => set(field, e.target.value)}
          placeholder={`Enter ${label.toLowerCase()}...`}
          disabled={!selectedSurgery}
        />
      )}
    </div>
  );

  return (
    <div>
      <SectionHeader icon="📋" title="OT Notes" subtitle="Record detailed intra-operative and post-operative notes for completed surgeries" />

      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="field">
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, fontSize: 13, color: '#374151' }}>
            Select Completed Surgery
          </label>
          <select
            className="input"
            style={{ maxWidth: 500 }}
            value={selectedId}
            onChange={e => handleSelect(e.target.value)}
          >
            <option value="">— Select a completed surgery to add notes —</option>
            {completedSurgeries.map(s => (
              <option key={s.id} value={s.id}>
                {s.id} — {s.patient} | {s.surgery} | {s.date}
                {otNotes.find(n => n.surgeryId === s.id) ? ' ✏️' : ''}
              </option>
            ))}
          </select>
          <div className="hint" style={{ marginTop: 4 }}>
            Only completed surgeries are available for notes entry.
          </div>
        </div>

        {selectedSurgery && (
          <div style={{
            marginTop: 12, padding: '10px 16px', borderRadius: 8,
            background: '#f8f9ff', border: '1px solid #e0e7ff',
            display: 'flex', flexWrap: 'wrap', gap: '6px 24px', fontSize: 13,
          }}>
            <span>👤 <strong>{selectedSurgery.patient}</strong> ({selectedSurgery.age}y)</span>
            <span>🔪 <strong>{selectedSurgery.surgery}</strong></span>
            <span>👨‍⚕️ {selectedSurgery.surgeon}</span>
            <span>🏥 {selectedSurgery.ot}</span>
            <span>🗓️ {selectedSurgery.date} {selectedSurgery.time}</span>
          </div>
        )}
      </div>

      {!selectedSurgery && (
        <div className="info-banner" style={{ marginBottom: 16 }}>
          ℹ️ Select a completed surgery from the dropdown above to begin entering post-operative notes.
        </div>
      )}

      <div className="card" style={{ padding: 24, opacity: selectedSurgery ? 1 : 0.55 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#374151' }}>📝 Intra-operative &amp; Post-operative Notes</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          <NoteField label="Findings" field="findings" multiline rows={4} />
          <NoteField label="Procedure Details" field="procedureDetails" multiline rows={4} />
        </div>
        <div className="form-grid" style={{ marginTop: 8 }}>
          <NoteField label="Implants Used" field="implantsUsed" />
          <NoteField label="Blood Loss" field="bloodLoss" />
          <NoteField label="IV Fluids" field="ivFluids" />
          <NoteField label="Specimens Sent" field="specimensSent" />
        </div>
        <div style={{ marginTop: 8 }}>
          <NoteField label="Additional Notes" field="additionalNotes" multiline rows={3} />
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!selectedSurgery || saving}
          >
            {saving ? '⏳ Saving…' : '💾 Save Notes'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setForm(BLANK_NOTES)}
            disabled={!selectedSurgery}
          >
            ↺ Clear
          </button>
          {saved && (
            <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 13 }}>
              ✅ Notes saved successfully!
            </span>
          )}
        </div>
      </div>

      {/* Saved notes preview */}
      {otNotes.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#374151' }}>📂 Saved Notes</h3>
          {otNotes.map((notes) => {
            const s = surgeries.find(x => x.id === notes.surgeryId);
            if (!s) return null;
            return (
              <div key={notes.id} className="card" style={{ padding: '16px 20px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <span style={{ fontWeight: 700, color: '#4f46e5', marginRight: 10 }}>{s.id}</span>
                    <span style={{ fontWeight: 600 }}>{s.patient}</span>
                    <span style={{ color: '#6b7280', marginLeft: 8, fontSize: 12 }}>— {s.surgery}</span>
                  </div>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleSelect(s.id)}
                  >
                    ✏️ Edit
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, fontSize: 12 }}>
                  {notes.findings && <div><strong>Findings:</strong> {notes.findings}</div>}
                  {notes.procedureDetails && <div><strong>Procedure:</strong> {notes.procedureDetails}</div>}
                  {notes.implantsUsed && <div><strong>Implants:</strong> {notes.implantsUsed}</div>}
                  {notes.bloodLoss && <div><strong>Blood Loss:</strong> {notes.bloodLoss}</div>}
                  {notes.ivFluids && <div><strong>IV Fluids:</strong> {notes.ivFluids}</div>}
                  {notes.specimensSent && <div><strong>Specimens:</strong> {notes.specimensSent}</div>}
                  {notes.additionalNotes && <div><strong>Notes:</strong> {notes.additionalNotes}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: OT Stats ────────────────────────────────────────────────────────────

function OtStatsTab({ surgeries }) {
  const stats = useMemo(() => {
    const total = surgeries.length;
    const emergency = surgeries.filter(s => s.priority === 'Emergency').length;
    const durations = surgeries.map(s => s.duration);
    const avgDuration = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
    const otUtilization = 72; // hardcoded as per spec

    // Breakdown by surgery type
    const byType = {};
    surgeries.forEach(s => {
      byType[s.surgery] = (byType[s.surgery] || 0) + 1;
    });

    // Breakdown by status
    const byStatus = {};
    surgeries.forEach(s => {
      byStatus[s.status] = (byStatus[s.status] || 0) + 1;
    });

    // By priority
    const byPriority = {};
    surgeries.forEach(s => {
      byPriority[s.priority] = (byPriority[s.priority] || 0) + 1;
    });

    // By surgeon
    const bySurgeon = {};
    surgeries.forEach(s => {
      bySurgeon[s.surgeon] = (bySurgeon[s.surgeon] || 0) + 1;
    });

    return { total, emergency, avgDuration, otUtilization, byType, byStatus, byPriority, bySurgeon };
  }, [surgeries]);

  const StatCard = ({ icon, label, value, unit, color }) => (
    <div className="card" style={{
      padding: '20px 24px',
      borderTop: `4px solid ${color}`,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ fontSize: 28 }}>{icon}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}<span style={{ fontSize: 16, fontWeight: 600, color: '#6b7280', marginLeft: 4 }}>{unit}</span>
      </div>
      <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <SectionHeader icon="📊" title="OT Statistics" subtitle="Operational metrics and surgical performance overview" />

      {/* Primary stats */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard icon="🔪" label="Total Surgeries" value={stats.total}        unit=""     color="#6366f1" />
        <StatCard icon="🚨" label="Emergency Count"  value={stats.emergency}    unit=""     color="#ef4444" />
        <StatCard icon="⏱️" label="Avg Duration"      value={stats.avgDuration}  unit="min"  color="#f59e0b" />
        <StatCard icon="📈" label="OT Utilization"    value={stats.otUtilization} unit="%"   color="#10b981" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Breakdown by surgery type */}
        <div className="card" style={{ padding: '20px 22px' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#374151' }}>🔪 By Surgery Type</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Surgery</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Count</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.byType)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <tr key={type} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 8px' }}><span className="emr-chip">{type}</span></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 700 }}>{count}</td>
                    <td style={{ padding: '8px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          height: 8, borderRadius: 4,
                          width: `${(count / stats.total) * 100}%`,
                          minWidth: 20, maxWidth: 100,
                          background: '#6366f1',
                        }} />
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{Math.round((count / stats.total) * 100)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Breakdown by status */}
          <div className="card" style={{ padding: '20px 22px' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#374151' }}>📌 By Status</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '5px 8px', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Status</th>
                  <th style={{ textAlign: 'center', padding: '5px 8px', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.byStatus).map(([status, count]) => (
                  <tr key={status} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '7px 8px' }}>{statusBadge(status)}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 700 }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Breakdown by priority */}
          <div className="card" style={{ padding: '20px 22px' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#374151' }}>⚡ By Priority</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '5px 8px', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Priority</th>
                  <th style={{ textAlign: 'center', padding: '5px 8px', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.byPriority).map(([priority, count]) => (
                  <tr key={priority} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '7px 8px' }}>{priorityBadge(priority)}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 700 }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Surgeon workload */}
      <div className="card" style={{ padding: '20px 22px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#374151' }}>👨‍⚕️ Surgeon Workload</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {Object.entries(stats.bySurgeon)
            .sort((a, b) => b[1] - a[1])
            .map(([surgeon, count]) => (
              <div key={surgeon} style={{
                padding: '10px 16px', borderRadius: 10, background: '#f0f4ff',
                border: '1px solid #c7d2fe', display: 'flex', flexDirection: 'column', gap: 4,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#3730a3' }}>{surgeon}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#4f46e5' }}>{count} <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>case{count !== 1 ? 's' : ''}</span></div>
              </div>
            ))}
        </div>
      </div>

      {/* Duration insight */}
      <div className="card" style={{ padding: '20px 22px', marginTop: 16 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#374151' }}>⏱️ Duration Breakdown</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {surgeries.map(s => (
            <div key={s.id} style={{
              padding: '10px 14px', borderRadius: 10,
              background: '#fffbeb', border: '1px solid #fde68a', minWidth: 170,
            }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#92400e' }}>{s.surgery}</div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{s.patient}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#d97706', marginTop: 4 }}>
                {s.duration}<span style={{ fontSize: 12, color: '#92400e', marginLeft: 3 }}>min</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#f8f9ff', border: '1px solid #e0e7ff', fontSize: 13, color: '#4f46e5', fontWeight: 600 }}>
          📊 Total OT time used today: {surgeries.filter(s => s.date === TODAY).reduce((a, b) => a + Number(b.duration || 0), 0)} minutes
          &nbsp;·&nbsp; Avg: {stats.avgDuration} min/case
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'schedule', label: '🗓️ OT Schedule' },
  { id: 'book',     label: '📝 Book Surgery' },
  { id: 'register', label: '📒 OT Register' },
  { id: 'notes',    label: '📋 OT Notes' },
  { id: 'stats',    label: '📊 OT Stats' },
];

export default function OtManagementPage() {
  const [activeTab, setActiveTab] = useState('schedule');
  const { records: surgeries, create, patch, reload } = useHmsRecords('ot-surgery');
  const [loading, setLoading] = useState(false);

  const { data: admData } = useLiveData(() => ipApi.getAdmissions(100), []);
  const admissions = (admData?.admissions || []).map(a => ({
    id: a.admissionId,
    name: a.patientName,
    gender: a.gender || '',
    age: a.age || '',
  }));

  const handleBook = async (newSurgery) => {
    setLoading(true);
    try {
      await create({
        ...newSurgery,
        status: newSurgery.status || 'Scheduled',
        date: newSurgery.date || TODAY,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSurgeryStatus = async (id, status, extra = {}) => {
    await patch(id, { status, ...extra });
    reload();
  };

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1280, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 24, paddingBottom: 18, borderBottom: '2px solid #e5e7eb',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', fontSize: 22,
            }}>🏥</span>
            OT Management
          </h1>
          <p style={{ margin: '6px 0 0 54px', fontSize: 13.5, color: '#6b7280' }}>
            Operation Theatre scheduling, surgical records, and performance analytics
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="emr-chip" style={{
            background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
            fontWeight: 700, fontSize: 12,
          }}>
            📅 {TODAY}
          </span>
          <span className="emr-chip" style={{
            background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd',
            fontWeight: 700, fontSize: 12,
          }}>
            {surgeries.length} Cases
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="period-tabs" style={{
        display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap',
        background: '#f3f4f6', padding: 4, borderRadius: 12, width: 'fit-content',
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`btn btn-sm ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`}
            style={{
              borderRadius: 8,
              ...(activeTab === tab.id ? {
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff', border: 'none',
                boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
              } : {
                background: 'transparent', color: '#6b7280',
                border: 'none',
              }),
              transition: 'all 0.18s ease',
              fontWeight: activeTab === tab.id ? 700 : 500,
              padding: '8px 16px',
              cursor: 'pointer',
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'schedule' && <OtScheduleTab surgeries={surgeries} />}
        {activeTab === 'book'     && <BookSurgeryTab onBook={handleBook} admissions={admissions} />}
        {activeTab === 'register' && <OtRegisterTab surgeries={surgeries} updateSurgeryStatus={updateSurgeryStatus} />}
        {activeTab === 'notes'    && <OtNotesTab surgeries={surgeries} />}
        {activeTab === 'stats'    && <OtStatsTab surgeries={surgeries} />}
      </div>
    </div>
  );
}
